import React from "react";
import { Link } from "react-router-dom";
import { experts } from "@/data/experts";
import Reveal from "@/components/Reveal";
import Grain from "@/components/Grain";
import SEO, { breadcrumbSchema, SITE_URL } from "@/components/SEO";

export default function ExpertMinds() {
  const expertListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Next in Luxury — Expert Minds",
    description:
      "Directory of luxury advisors and expert minds at Next in Luxury — brand strategy, experience design, service excellence, hospitality, retail, culture and leadership.",
    itemListElement: experts.map((e, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      url: `${SITE_URL}/expert-minds/${e.slug}`,
      name: e.name,
    })),
  };

  return (
    <div
      data-testid="page-experts"
      className="pt-24"
      style={{ background: "#F4EFE7", minHeight: "100vh" }}
    >
      <SEO
        title="Expert Minds — Luxury Advisors & Consultants"
        description="Meet the Expert Minds of Next in Luxury — an international network of luxury advisors and consultants in brand strategy, hospitality, retail, experience design, service excellence and cultural leadership."
        path="/expert-minds"
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Expert Minds", path: "/expert-minds" },
          ]),
          expertListSchema,
        ]}
      />
      {/* ═══ COVER (Ivory) ═══ */}
      <section className="relative" style={{ background: "#F4EFE7" }}>
        <Grain opacity={0.03} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 pt-24 pb-16">
          {/* Title */}
          <div className="grid grid-cols-12 gap-8 pt-8">
            <div className="col-span-12 md:col-span-2">
              <Reveal>
                <p
                  className="font-eyebrow"
                  style={{ color: "#8C6B3F", letterSpacing: "0.32em" }}
                >
                  Our Expert Minds
                </p>
                <div
                  className="hairline mt-4"
                  style={{ width: "24px", background: "#8C6B3F" }}
                />
              </Reveal>
            </div>

            <div className="col-span-12 md:col-span-10">
              <Reveal delay={120}>
                <h1
                  className="title-caps"
                  style={{
                    fontSize: "clamp(2.5rem, 8vw, 8.5rem)",
                    lineHeight: 0.92,
                    letterSpacing: "-0.02em",
                    textWrap: "balance",
                    color: "#14140F",
                  }}
                  data-testid="experts-title"
                >
                  THE{" "}
                  <span
                    className="font-script"
                    style={{
                      textTransform: "none",
                      letterSpacing: 0,
                      fontSize: "1.1em",
                      color: "#8C6B3F",
                      display: "inline-block",
                      transform: "translateY(0.1em)",
                    }}
                  >
                    people
                  </span>
                  <br />
                  BEHIND EXCEPTIONAL BRANDS.
                </h1>
              </Reveal>

              <Reveal delay={280}>
                <p
                  className="font-serif-italic mt-16"
                  style={{
                    fontSize: "clamp(1.15rem, 1.4vw, 1.4rem)",
                    lineHeight: 1.55,
                    color: "#14140F",
                    opacity: 0.85,
                    maxWidth: "58ch",
                    textWrap: "balance",
                    fontVariationSettings: '"SOFT" 60, "WONK" 1, "opsz" 72',
                  }}
                >
                  Guided by the masters who are shaping the future of luxury and
                  trusted by the world&rsquo;s most iconic maisons and
                  institutions. We are an elite group of consultants and
                  advisors with decades of experience across multiple
                  disciplines within the luxury ecosystem. With global presence,
                  we collaborate on iconic projects, bringing a unique
                  perspective and a refined understanding of luxury that
                  transforms businesses into industry benchmarks. Our
                  contribution goes beyond education and advice: we refine
                  strategy for giga-projects and institutions, we transmit
                  savoir-faire through our training and we take brands from
                  excellent to exceptional &mdash; and we help them to stay at
                  the top.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ THE ROSTER — Editorial Index ═══ */}
      <section
        className="relative"
        style={{ background: "#F4EFE7" }}
        data-testid="experts-index"
      >
        <Grain opacity={0.028} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 pb-32">
          {/* Roster header removed per user request */}
          <div
            className="pt-16 pb-14"
            style={{ borderTop: "1px solid #B8B2A6" }}
          />

          {/* Grid — 3 columns desktop, cleaner masthead feel */}
          <div className="grid grid-cols-12 gap-x-10 gap-y-24">
            {experts.map((e, idx) => (
              <Reveal
                key={e.slug}
                delay={idx * 40}
                className="col-span-12 sm:col-span-6 md:col-span-4"
              >
                <Link
                  to={`/expert-minds/${e.slug}`}
                  data-testid={`expert-card-${e.slug}`}
                  className="block group"
                >
                  {/* Portrait — uniform editorial framing */}
                  <div className="p-2 md:p-3" style={{ border: "1px solid #B8B2A6" }}>
                    <div
                      className="portrait-frame aspect-[4/5] overflow-hidden"
                      style={{ background: "#EDE6D6" }}
                    >
                      <img
                        src={e.portrait}
                        alt={e.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                          objectFit: "cover",
                          objectPosition: e.portraitFocus || "center 20%",
                          filter:
                            "grayscale(1) contrast(1.18) brightness(0.94) saturate(0)",
                          transition: "filter 700ms ease, transform 900ms ease",
                        }}
                      />
                    </div>
                  </div>

                  {/* Name */}
                  <h3
                    className="title-caps mt-8 leading-[0.95]"
                    style={{
                      fontSize: "clamp(1.5rem, 2.4vw, 2.1rem)",
                      color: "#14140F",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {e.name.split(" ")[0].toUpperCase()}
                    <br />
                    <span
                      className="font-script"
                      style={{
                        textTransform: "none",
                        letterSpacing: 0,
                        fontSize: "1.15em",
                        color: "#8C6B3F",
                      }}
                    >
                      {e.name.split(" ").slice(1).join(" ")}
                    </span>
                  </h3>

                  {/* Discipline (small caps) */}
                  <div className="mt-6">
                    <p
                      className="font-eyebrow"
                      style={{
                        color: "#14140F",
                        opacity: 0.7,
                        letterSpacing: "0.28em",
                        fontSize: "0.72rem",
                        lineHeight: 1.5,
                      }}
                    >
                      {e.role}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CORRESPONDENCE (Ivory) ═══ */}
      <section
        className="relative"
        style={{ background: "#F4EFE7" }}
        data-testid="experts-correspondence"
      >
        <Grain opacity={0.028} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 py-24">
          <div
            className="grid grid-cols-12 gap-8 pt-16"
            style={{ borderTop: "1px solid #B8B2A6" }}
          >
            <div className="col-span-12 md:col-span-2">
              <p
                className="font-eyebrow"
                style={{ color: "#8C6B3F", letterSpacing: "0.32em" }}
              >
                Correspondence
              </p>
              <div
                className="hairline mt-4"
                style={{ width: "24px", background: "#8C6B3F" }}
              />
            </div>
            <div className="col-span-12 md:col-span-7">
              <p
                className="font-serif-italic"
                style={{
                  fontSize: "clamp(1.1rem, 1.3vw, 1.35rem)",
                  lineHeight: 1.6,
                  color: "#14140F",
                  opacity: 0.82,
                  maxWidth: "56ch",
                  textWrap: "balance",
                }}
              >
                Direct inquiries to the Circle are welcomed through the House.
              </p>
              <div className="mt-10">
                <Link
                  to="/private-inquiry"
                  className="editorial-link font-eyebrow"
                  style={{
                    color: "#8C6B3F",
                    letterSpacing: "0.28em",
                    fontSize: "0.72rem",
                  }}
                  data-testid="experts-inquiry-link"
                >
                  Send a Private Inquiry &nbsp;⟶
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CONTINUE — to Journal ═══ */}
      <section
        className="max-w-[1440px] mx-auto px-6 md:px-16 lg:px-24 py-24 flex justify-between items-end"
        style={{ background: "#F4EFE7" }}
      >
        <div>
          <p className="font-eyebrow opacity-60">Continue</p>
          <h2
            className="font-serif-display mt-4 leading-none"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            The{" "}
            <span
              className="font-script"
              style={{
                textTransform: "none",
                letterSpacing: 0,
                color: "#8C6B3F",
              }}
            >
              Journal
            </span>
          </h2>
        </div>
        <Link
          to="/journal"
          className="btn-ghost"
          data-testid="experts-continue-link"
        >
          Read Next <span className="arrow">⟶</span>
        </Link>
      </section>
    </div>
  );
}
